import { CloseIcon, ScalesIcon } from '@repo/atomic-components';
import { classNames as cn } from 'primereact/utils';
import type { CaseData } from '@/api/cases/getCases';
import { useFrostedGlassClass } from '@/ui/theme/useFrostedGlassClass';

interface CaseMentionChipProps {
  caseData: CaseData;
  onRemove: () => void;
}

/** Chip for the case linked to a new chat, styled like the file upload chips. At most one per chat. */
const CLIENT_MAX = 64;
const CASE_REF_MAX = 24;

const truncateWithEllipsis = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

export const CaseMentionChip = ({ caseData, onRemove }: CaseMentionChipProps) => {
  const frostedBgClass = useFrostedGlassClass();
  const client = truncateWithEllipsis(caseData.client || 'Linked case', CLIENT_MAX);
  const caseRef = caseData.caseReference ? truncateWithEllipsis(caseData.caseReference, CASE_REF_MAX) : undefined;
  const ariaLabel = caseRef ? `${client} ${caseRef}` : client;
  return (
    <div
      className={cn(
        'border-theme-border-grey/50 text-theme-black upload-chip-shadow mb-2 inline-flex h-8 items-center gap-2 rounded-md border py-1 pr-1 pl-2.5',
        frostedBgClass,
      )}
    >
      <span className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center">
        <ScalesIcon width={18} height={18} />
      </span>
      <span className="flex items-baseline gap-1.5 text-[0.8125rem]">
        <span className="font-semibold">{client}</span>
        {caseRef && <span className="text-theme-text-grey font-normal">{caseRef}</span>}
      </span>
      <button
        type="button"
        aria-label={`Remove case ${ariaLabel}`}
        onClick={onRemove}
        className="text-theme-text-grey hover:bg-theme-background-grey hover:text-theme-black flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors"
      >
        <CloseIcon width={8} height={8} className="shrink-0" />
      </button>
    </div>
  );
};
